import React, { useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useFormikContext } from "formik";

import OrderedFieldsSection from "./OrderedFieldsSection";
import useStoreCardFieldsRules from "../../../hooks/useStoreCardFieldsRules";
import { buildLegacyStoreCardOrderedFields } from "../../../views/shopping/ProjectAssets/storeCardFields";

/** Explains the legacy default layout when `ordered_fields` is empty (see AssetCard / AssetCardTitle). */
function StoreCardDefaultLayoutExplainer() {
  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        The store card defaults to showing the fields listed below:
      </Typography>
      <Box
        component="ul"
        sx={{
          m: 0,
          pl: 2.25,
          color: "text.secondary",
          typography: "body2",
          "& li": { mb: 0.5 },
        }}
      >
        <li>SKU Number and Item Code</li>
        <li>Manufacturer, Description, Model, and Dimensions</li>
        <li>PO Number, Color, and Condition</li>
        <li>Comments and Expiration</li>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
        To customize this layout, use &quot;Set Custom Store Card&quot; below. You can reorder, remove, or add
        fields afterward.
      </Typography>
    </Box>
  );
}

export default function StoreCardFieldsSettings() {
  const { values, setFieldValue } = useFormikContext();
  const availableFields = useStoreCardFieldsRules(values?.fieldSettings);
  const orderedFields = values?.storeCardFields?.ordered_fields || [];

  const selectedFields = useMemo(
    () =>
      orderedFields
        .map(fieldName => availableFields.find(field => field[0] === fieldName))
        .filter(Boolean),
    [availableFields, orderedFields]
  );

  const handleReorder = reorderedFields => {
    setFieldValue(
      "storeCardFields.ordered_fields",
      reorderedFields.map(field => field[0])
    );
  };

  const handleRemove = fieldId => {
    setFieldValue(
      "storeCardFields.ordered_fields",
      orderedFields.filter(fieldName => fieldName !== fieldId)
    );
  };

  const handleAdd = field => {
    setFieldValue("storeCardFields.ordered_fields", [...orderedFields, field[0]]);
  };

  const legacySeedOrderedFields = useMemo(
    () => buildLegacyStoreCardOrderedFields(availableFields),
    [availableFields]
  );

  const canSetCustomStoreCard = legacySeedOrderedFields.length > 0;

  const handleSetCustomStoreCard = () => {
    if (legacySeedOrderedFields.length === 0) return;
    setFieldValue("storeCardFields.ordered_fields", legacySeedOrderedFields);
  };

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <OrderedFieldsSection
        title="Store Card Fields"
        fields={selectedFields}
        onReorder={handleReorder}
        onRemove={handleRemove}
        onAdd={handleAdd}
        availableFields={availableFields}
        addButtonLabel="Add Another Field to Card"
        emptyStateContent={<StoreCardDefaultLayoutExplainer />}
        emptyStatePrimaryAction={{
          label: "Set Custom Store Card",
          onClick: handleSetCustomStoreCard,
          disabled: !canSetCustomStoreCard,
          disabledTooltip:
            "None of the default store card fields are enabled for this project. Enable them under Inventory Fields, or add fields individually after choosing a starting layout.",
        }}
      />
    </Box>
  );
}
